import React from 'react';

// MUI
import { Typography } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// Utils
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// Styles
import './styles.scss';

// Generic table to be reused across Pricing Controls sections
const PricingControlsTable = ({
  rows = [],
  columns,
  columnGroupingModel,
  columnGroupHeaderHeight,
  pagination,
  onPageChange,
  onPageSizeChange,
  updatePagination,
  showPagination = false,
  className = '',
  columnHeaderHeight = 48,
  autoHeight = true,
  disabledFlagKey = 'disabled',
  disabledRowClass = 'disabled-row',
  editMode = 'cell',
  processRowUpdate,
  onProcessRowUpdateError,
  isCellEditable,
  loading = false,
  showNoRows = false,
}) => {
  const groupingProps = columnGroupingModel
    ? {
        columnGroupHeaderHeight,
        columnGroupingModel,
        experimentalFeatures: { columnGrouping: true },
      }
    : {};

  return (
    <div
      style={{
        marginTop: '16px',
        width: '100%',
        position: 'relative',
      }}
      className={`userAdmin olcAdminListTable ${className} ${loading ? 'table-loading' : ''}`}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        autoHeight={autoHeight}
        pagination={pagination}
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        disableColumnMenu={true}
        className="userAdminTable"
        getRowId={(row) => row?.id}
        getRowClassName={(params) => (params?.row?.[disabledFlagKey] ? disabledRowClass : '')}
        columnHeaderHeight={columnHeaderHeight}
        {...groupingProps}
        editMode={editMode}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        isCellEditable={isCellEditable}
        loading={loading}
        slots={{
          // Match UserTable behavior: only show "No Results Found" when not loading
          noRowsOverlay: () =>
            showNoRows && !loading ? (
              <Typography className="noRowsTextContacts">No Results Found</Typography>
            ) : null,
          noResultsOverlay: () =>
            showNoRows && !loading ? (
              <Typography className="noRowsTextContacts">No Results Found</Typography>
            ) : null,
        }}
        sx={{
          maxHeight: '860px !important',
          '& .MuiDataGrid-main': {
            overflow: 'hidden !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'hidden !important',
            overflowY: 'auto !important',
            '@media (max-width: 1600px)': {
              overflowX: 'auto !important',
            },
          },
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'transparent !important',
            backdropFilter: 'none !important',
          },
        }}
      />
      {showPagination && (loading || pagination?.totalRecords > 0) && (
        <OlcTablePagination
          count={pagination?.lastPage || 1}
          page={pagination?.page || 1}
          pageSize={
            pagination?.pageSize && DEFAULT_PAGE_SIZES.includes(Number(pagination.pageSize))
              ? Number(pagination.pageSize)
              : DEFAULT_PAGE_SIZES[0]
          }
          pageSizes={DEFAULT_PAGE_SIZES}
          onPageChange={(_, page) => {
            if (updatePagination) {
              updatePagination({
                page,
                pageSize: pagination.pageSize,
              });
            } else {
              onPageChange?.(page);
            }
          }}
          onPageSizeChange={(event) => {
            const pageSize = Number(event.target.value);
            if (updatePagination) {
              updatePagination({
                page: 1,
                pageSize,
              });
            } else {
              onPageSizeChange?.(pageSize);
            }
          }}
          from={pagination?.from || 0}
          to={pagination?.to || 0}
          total={pagination?.totalRecords || 0}
          loading={loading}
        />
      )}
    </div>
  );
};

export default PricingControlsTable;
